/**
 * Comportamentos das seções quiz-* (template product.quiz, visual V21).
 *
 *   <quiz-carousel>   banner com setas, indicadores e arrasto (sections/quiz-banner.liquid)
 *   <quiz-marquee>    depoimentos em movimento contínuo, com botão pausar (sections/quiz-testimonials.liquid)
 *   <quiz-gallery>    vídeos com setas e play com som (sections/quiz-videos.liquid)
 *   <quiz-motion>     vídeo mudo que toca só quando visível (sections/quiz-motion.liquid)
 *   <quiz-sticky-bar> barra fixa do celular, escondida enquanto o quiz está na tela (sections/quiz-sticky-bar.liquid)
 */

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const scrollBehavior = () => (reducedMotion() ? 'instant' : 'smooth');

class QuizCarousel extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;
  #active = 0;
  #frame = 0;
  /** @type {ResizeObserver | undefined} */
  #resize;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const track = this.#track;
    if (!track) return;

    this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.#active - 1), { signal });
    this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.#active + 1), { signal });
    this.querySelectorAll('[data-dot]').forEach((dot, index) =>
      dot.addEventListener('click', () => this.go(index), { signal })
    );
    track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(this.#frame);
        this.#frame = requestAnimationFrame(() => this.#sync());
      },
      { passive: true, signal }
    );
    track.addEventListener(
      'keydown',
      (event) => {
        if (event.target !== track) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          this.go(this.#active + (event.key === 'ArrowRight' ? 1 : -1));
        }
      },
      { signal }
    );

    let width = track.clientWidth;
    this.#resize = new ResizeObserver(() => {
      if (width === track.clientWidth) return;
      width = track.clientWidth;
      track.scrollTo({ left: this.#active * width, behavior: 'instant' });
    });
    this.#resize.observe(track);
    this.#sync();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#resize?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  get #track() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-track]'));
  }

  get #slides() {
    return Array.from(this.querySelectorAll('[data-slide]'));
  }

  /** @param {number} index */
  go(index) {
    const track = this.#track;
    const total = this.#slides.length;
    if (!track || !total) return;
    const next = (index + total) % total;
    track.scrollTo({ left: next * track.clientWidth, behavior: scrollBehavior() });
  }

  #sync() {
    const track = this.#track;
    const slides = this.#slides;
    if (!track || !slides.length) return;
    this.#active = Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / (track.clientWidth || 1))));
    slides.forEach((slide, i) => {
      slide.toggleAttribute('inert', i !== this.#active);
    });
    this.querySelectorAll('[data-dot]').forEach((dot, i) => {
      if (i === this.#active) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
    const status = this.querySelector('[data-status]');
    if (status) status.textContent = `${this.#active + 1} / ${slides.length}`;
  }
}

class QuizMarquee extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;
  /** @type {IntersectionObserver | undefined} */
  #observer;
  #frame = 0;
  #last = 0;
  #position = 0;
  #pausedByUser = false;
  #hovered = false;
  #focused = false;
  #visible = false;

  connectedCallback() {
    const track = /** @type {HTMLElement | null} */ (this.querySelector('[data-track]'));
    const toggle = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-toggle]'));
    if (!track) return;

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#pausedByUser = reducedMotion();

    // Cópias inertes para o loop contínuo; não entram na leitura nem no foco.
    if (!track.querySelector('[data-clone]')) {
      Array.from(track.children).forEach((card) => {
        const clone = /** @type {HTMLElement} */ (card.cloneNode(true));
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('data-clone', '');
        clone.inert = true;
        track.append(clone);
      });
    }

    this.addEventListener('pointerenter', () => (this.#hovered = true), { signal });
    this.addEventListener('pointerleave', () => (this.#hovered = false), { signal });
    this.addEventListener('focusin', () => (this.#focused = true), { signal });
    this.addEventListener('focusout', () => (this.#focused = false), { signal });
    // Só acompanha a rolagem feita pela cliente; a do loop usa frações de pixel que o navegador arredonda.
    track.addEventListener('scroll', () => {
      if (Math.abs(track.scrollLeft - this.#position) > 2) this.#position = track.scrollLeft;
    }, { passive: true, signal });

    if (toggle) {
      toggle.hidden = false;
      toggle.addEventListener('click', () => {
        this.#pausedByUser = !this.#pausedByUser;
        this.#renderToggle(toggle);
      }, { signal });
      this.#renderToggle(toggle);
    }

    this.#observer = new IntersectionObserver((entries) => {
      this.#visible = entries.some((entry) => entry.isIntersecting);
    });
    this.#observer.observe(this);

    const step = (time) => {
      const delta = this.#last ? Math.min(time - this.#last, 64) : 0;
      this.#last = time;
      const running = !this.#pausedByUser && !this.#hovered && !this.#focused && this.#visible && !document.hidden;
      if (running) {
        // Distância até a primeira cópia = largura exata de um ciclo (inclui os espaços entre cartões).
        const first = /** @type {HTMLElement | null} */ (track.firstElementChild);
        const firstClone = /** @type {HTMLElement | null} */ (track.querySelector('[data-clone]'));
        const loop = first && firstClone ? firstClone.offsetLeft - first.offsetLeft : track.scrollWidth / 2;
        this.#position += delta * 0.03;
        if (this.#position >= loop) this.#position -= loop;
        track.scrollLeft = this.#position;
      }
      this.#frame = requestAnimationFrame(step);
    };
    this.#frame = requestAnimationFrame(step);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  /** @param {HTMLButtonElement} toggle */
  #renderToggle(toggle) {
    toggle.setAttribute('aria-pressed', String(this.#pausedByUser));
    const label = toggle.querySelector('[data-toggle-label]');
    const icon = toggle.querySelector('[data-toggle-icon]');
    if (label) label.textContent = this.#pausedByUser ? toggle.dataset.playLabel || 'Retomar' : toggle.dataset.pauseLabel || 'Pausar';
    if (icon) icon.textContent = this.#pausedByUser ? '▶' : 'Ⅱ';
  }
}

class QuizGallery extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;
  /** @type {ResizeObserver | undefined} */
  #resize;

  connectedCallback() {
    const track = /** @type {HTMLElement | null} */ (this.querySelector('[data-track]'));
    if (!track) return;
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    const prev = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-prev]'));
    const next = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-next]'));
    const update = () => {
      if (prev) prev.disabled = track.scrollLeft < 2;
      if (next) next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
    };
    const move = (direction) => {
      const card = track.firstElementChild;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const width = card ? card.getBoundingClientRect().width + gap : track.clientWidth;
      track.scrollBy({ left: direction * width, behavior: scrollBehavior() });
    };
    prev?.addEventListener('click', () => move(-1), { signal });
    next?.addEventListener('click', () => move(1), { signal });
    track.addEventListener('scroll', update, { passive: true, signal });
    this.#resize = new ResizeObserver(update);
    this.#resize.observe(track);
    update();

    this.querySelectorAll('[data-video-card]').forEach((card) => {
      const video = card.querySelector('video');
      const play = /** @type {HTMLButtonElement | null} */ (card.querySelector('[data-play]'));
      if (!video || !play) return;
      play.addEventListener('click', async () => {
        this.querySelectorAll('video').forEach((other) => {
          if (other !== video) other.pause();
        });
        try {
          video.controls = true;
          await video.play();
          play.hidden = true;
        } catch {
          play.hidden = false;
        }
      }, { signal });
      video.addEventListener('ended', () => {
        video.controls = false;
        play.hidden = false;
      }, { signal });
    });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#resize?.disconnect();
  }
}

class QuizMotion extends HTMLElement {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    const video = this.querySelector('video');
    if (!video) return;
    this.#observer = new IntersectionObserver((entries) => {
      const visible = entries.some((entry) => entry.isIntersecting);
      if (visible && !reducedMotion()) {
        if (video.preload === 'none') video.preload = 'auto';
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    }, { threshold: 0.25 });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }
}

class QuizStickyBar extends HTMLElement {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    const target = document.querySelector(this.dataset.hideWhen || '#quiz');
    if (!target) return;
    this.#observer = new IntersectionObserver((entries) => {
      this.toggleAttribute('data-hidden', entries.some((entry) => entry.isIntersecting));
    });
    this.#observer.observe(target);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }
}

const elements = {
  'quiz-carousel': QuizCarousel,
  'quiz-marquee': QuizMarquee,
  'quiz-gallery': QuizGallery,
  'quiz-motion': QuizMotion,
  'quiz-sticky-bar': QuizStickyBar,
};

for (const [name, element] of Object.entries(elements)) {
  if (!customElements.get(name)) customElements.define(name, element);
}
